
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as PropTypes from 'prop-types';
import { progressControl } from '../../../controls/progressControl';
import {
  progressRootItemKey,
  getItemCountsByProgressPhase,
  expireProgressItems,
  getOverallSimpleProgressBreakdown,
  getOverallProgressPhase
} from '../../../operations/progress';
import { css } from '@uifabric/utilities/lib/css';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { Callout, DirectionalHint } from 'office-ui-fabric-react/lib/Callout';
import { Layer } from 'office-ui-fabric-react/lib/Layer';
import { ActionButton } from 'office-ui-fabric-react/lib/components/Button/ActionButton/ActionButton';
import * as ProgressCommandButtonStyles from './ProgressCommandButton.scss';
import ProgressStrings from '../ProgressStrings.resx';
import { format, getLocalizedCountValue, formatWithLocalizedCountValue } from '@ms/utilities-strings/lib/index';
import { ProgressList } from '../list/ProgressList';
import { makeObject } from '../../../utilities/objects';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { IItemCacheDispatch } from '../../../store/actions/ItemCacheActions';
import { ProgressPhase, Histogram } from '../../../facets/ProgressFacets';
import { childItemsFacet } from '../../../facets/SetFacets';
import { Async } from '@uifabric/utilities/lib/Async';
import { Fragment } from '../../../utilities/components/Fragment';
import { ICommandBarItemRenderProps } from '../../commands/commandBarItemDefaultRender';
import { IComponentAsProps } from '@uifabric/utilities/lib/IComponentAs';

const ICON_NAMES: {
  [K in ProgressPhase]?: string;
} = {
  started: 'Sync',
  completed: 'CheckMark',
  failed: 'StatusErrorFull',
  canceled: 'Sync'
};

const formatters = {
  [ProgressPhase.started]: (count: number) => format(getLocalizedCountValue(
    ProgressStrings.genericStartedItemsTitle,
    ProgressStrings.genericCompletedItemsTitleIntervals,
    count) || '',
    count),
  [ProgressPhase.completed]: (count: number) => format(getLocalizedCountValue(
    ProgressStrings.genericCompletedItemsTitle,
    ProgressStrings.genericCompletedItemsTitleIntervals,
    count) || '',
    count),
  [ProgressPhase.failed]: (count: number) => format(getLocalizedCountValue(
    ProgressStrings.genericFailedItemsTitle,
    ProgressStrings.genericFailedItemsTitleIntervals,
    count) || '',
    count),
  [ProgressPhase.canceled]: (count: number) => format(getLocalizedCountValue(
    ProgressStrings.genericFailedItemsTitle,
    ProgressStrings.genericFailedItemsTitleIntervals,
    count) || '',
    count),
};

interface IProgressCalloutProps {
  target: HTMLElement;
  itemKeys: string[];
  itemKeysToExpire: string[];
  itemCacheDispatch: IItemCacheDispatch;
  isExecuting: boolean;
  doNotExpire: boolean;
  onDismiss: () => void;
  onViewAll?: IContextualMenuItem['onClick'];
}

interface IProgressCalloutState {
  dismissed?: boolean;
}

const ProgressCallout = connectToItemCache((itemCache: IItemCache, props: {
  itemKeys: string[];
}) => {
  const itemKeysToExpire = props.itemKeys.filter((itemKey: string) => {
    const progressPhase = getOverallProgressPhase(itemCache, { itemKey });

    return progressPhase === ProgressPhase.completed || progressPhase === ProgressPhase.canceled;
  });

  return {
    itemKeysToExpire: itemKeysToExpire
  };
})(class extends React.PureComponent<IProgressCalloutProps, IProgressCalloutState> {
  constructor(props: IProgressCalloutProps) {
    super(props);

    this.state = {
      dismissed: true
    };
  }

  public componentWillReceiveProps(nextProps: IProgressCalloutProps): void {
    if (nextProps.isExecuting) {
      return;
    }

    const items = makeObject(this.props.itemKeys, () => true);

    let hasNewItems = false;

    for (const itemKey of nextProps.itemKeys) {
      if (!(itemKey in items)) {
        hasNewItems = true;
      }
    }

    if (hasNewItems) {
      this.setState({
        dismissed: false
      });
    }
  }

  public render(): JSX.Element | null {
    const {
      target,
      itemKeys
    } = this.props;

    return !this.props.isExecuting && !this.state.dismissed && itemKeys.length > 0 ? (
      <Callout
        target={ target }
        role='alert'
        aria-live='assertive'
        directionalHint={ DirectionalHint.bottomCenter }
        onDismiss={ this._onDismiss }
        isBeakVisible={ false }
      >
        <div className={ ProgressCommandButtonStyles.callout }>
          <div className={ ProgressCommandButtonStyles.progressList }>
            <ProgressList
              groupsOnly={ true }
              itemKeys={ itemKeys.slice(-1) }
            />
          </div>
          <div className={ ProgressCommandButtonStyles.viewAllButton }>
            {
              itemKeys.length > 1 ? (
                <ActionButton
                  onClick={ this._onClickViewAll }
                >
                  <span className={ ProgressCommandButtonStyles.viewAll }>
                    { ProgressStrings.progressViewAllActionText }
                  </span>
                </ActionButton>
              ) : null
            }
          </div>
          <div className={ ProgressCommandButtonStyles.accessibleStatus }>
            { ProgressStrings.showActionDetailsPanelAccessibleHint }
          </div>
        </div>
      </Callout>
    ) : null;
  }

  public componentDidUpdate(previousProps: IProgressCalloutProps, previousState: IProgressCalloutState): void {
    if (!previousState.dismissed && this.state.dismissed) {
      if (!this.props.doNotExpire) {
        this.props.itemCacheDispatch(expireProgressItems({
          items: makeObject(this.props.itemKeysToExpire, () => true)
        }));
      }

      this.props.onDismiss();
    }
  }

  private _onDismiss = (): void => {
    this.setState({
      dismissed: true
    });
  }

  private _onClickViewAll = (ev?: any): void => { // tslint:disable-line:no-any
    if (ev) {
      ev.stopPropagation();
    }

    const {
      onViewAll
    } = this.props;

    if (onViewAll) {
      onViewAll(ev);
    }
  }
});

interface IProgressStatusProps {
  status: React.ReactNode;
  percentComplete: number | undefined;
}

interface IProgressStatusState {
  status: React.ReactNode;
  percentComplete: number | undefined;
}

class ProgressStatus extends React.PureComponent<IProgressStatusProps, IProgressStatusState> {
  private _async: Async;

  private _updateStatus: () => void;
  private _updatePercentComplete: () => void;

  constructor(props: IProgressStatusProps) {
    super(props);

    this.state = {
      status: props.status,
      percentComplete: props.percentComplete
    };

    this._async = new Async(this);

    this._updateStatus = this._async.throttle(() => {
      this.setState({
        status: this.props.status,
        percentComplete: this.props.percentComplete
      });
    }, 500, { leading: false, trailing: true });

    this._updatePercentComplete = this._async.throttle(() => {
      this.setState({
        status: this.props.status,
        percentComplete: this.props.percentComplete
      });
    }, 10000, { leading: false, trailing: true });
  }

  public render(): JSX.Element {
    const {
      status,
      percentComplete
    } = this.state;

    return (
      <Layer>
        <span
          className={ ProgressCommandButtonStyles.accessibleStatus }
          role='status'
          aria-live='polite'
          aria-atomic='true'
        >
          { status }
          { ' ' }
          { getPercentCompleteDescription(percentComplete) }
        </span>
      </Layer>
    );
  }

  public componentDidUpdate(previousProps: IProgressStatusProps): void {
    if (previousProps.status !== this.props.status) {
      this._updateStatus();
    } else if (previousProps.percentComplete !== this.props.percentComplete) {
      this._updatePercentComplete();
    }
  }

  public componentWillUnmount(): void {
    this._async.dispose();
  }
}

/**
 * @public
 */
export type IProgressCommandButtonBaseProps = IComponentAsProps<ICommandBarItemRenderProps> & {
  itemCacheDispatch: IItemCacheDispatch;
  finishedProgressItemKeys: string[];
  iconName: string;
  isStarted: boolean;
  isExecuting: boolean;
  progressTitle: React.ReactNode | undefined;
  progressPercentComplete: number | undefined;
};

/**
 * @public
 */
export interface IProgressCommandButtonState {
  root: HTMLElement | null;
  doNotExpire: boolean;
}

/**
 * @public
 */
export type IProgressCommandButtonProps =
  IComponentAsProps<ICommandBarItemRenderProps> & Pick<IProgressCommandButtonBaseProps, 'isExecuting'>;

/**
 * @public
 */
export const ProgressCommandButton: React.ComponentType<IProgressCommandButtonProps> =
  connectToItemCache((
    itemCache: IItemCache
  ): Pick<IProgressCommandButtonBaseProps,
  'finishedProgressItemKeys' | 'iconName' | 'isStarted' | 'progressTitle' | 'progressPercentComplete'
  > => {
    const progressChildItems = itemCache.demandItemFacet(childItemsFacet, progressRootItemKey);

    const progressItemKeys = progressChildItems && progressChildItems.itemKeys || [];

    let currentProgressItemKey: string | undefined = undefined;

    const finishedProgressItemKeys: string[] = [];

    for (const progressItemKey of progressItemKeys) {
      const progressPhase = getOverallProgressPhase(itemCache, { itemKey: progressItemKey });

      switch (progressPhase) {
        case ProgressPhase.created:
        case ProgressPhase.started:
          currentProgressItemKey = progressItemKey;
          break;
        case ProgressPhase.completed:
        case ProgressPhase.failed:
          finishedProgressItemKeys.push(progressItemKey);
          break;
        case ProgressPhase.canceled:
        case ProgressPhase.expired:
          break;
      }
    }

    const controlHandler = itemCache.demandItemFacet(progressControl, currentProgressItemKey);

    const {
      phase: iconPhase
    } = getOverallSimpleProgressBreakdown(itemCache, { itemKey: progressRootItemKey });

    let title: React.ReactNode;
    let percentComplete: number | undefined;

    if (currentProgressItemKey) {
      const currentItemCountsByProgressPhase = getItemCountsByProgressPhase(itemCache, {
        itemKey: currentProgressItemKey
      });

      const {
        phase,
        count
      } = getOverallSimpleProgressBreakdown(itemCache, { itemKey: currentProgressItemKey });

      ({
        title: title,
        percentComplete: percentComplete
      } = progressControl.evaluate(controlHandler)(itemCache, {
        itemKey: currentProgressItemKey,
        itemCountsByPhase: currentItemCountsByProgressPhase,
        title: formatters[phase](count),
        percentComplete: getPercentComplete(currentItemCountsByProgressPhase),
        format: 'summary'
      }));
    }

    const iconName = ICON_NAMES[iconPhase] || 'Sync';

    return {
      iconName,
      isStarted: iconPhase === 'started',
      progressPercentComplete: percentComplete,
      progressTitle: title,
      finishedProgressItemKeys
    };
  })(class extends React.PureComponent<IProgressCommandButtonBaseProps, IProgressCommandButtonState> {
    public static readonly contextTypes: React.ValidationMap<{
      isMeasured: boolean;
    }> = {
        isMeasured: PropTypes.bool
      };

    public readonly context!: {
      isMeasured: boolean;
    };

    constructor(props: IProgressCommandButtonBaseProps) {
      super(props);

      this.state = {
        root: null,
        doNotExpire: false
      };
    }

    public render(): JSX.Element | null {
      const {
        iconName,
        isStarted,
        finishedProgressItemKeys,
        children,
        defaultRender: DefaultRender,
        item,
        progressTitle,
        progressPercentComplete,
        ...buttonProps
      } = this.props;

      const {
        root
      } = this.state;

      return DefaultRender ? (
        <DefaultRender
          // This ref is only for the DOM element, not the component.
          buttonRef={ this._onButtonRef }
          item={
            {
              ...item,
              onClick: this._onClick
            }
          }
          { ...buttonProps }
          iconProps={
            {
              iconName: iconName,
              className: css({
                [ProgressCommandButtonStyles.created]: isStarted
              })
            }
          }
        >
          {
            progressTitle ? (
              <Fragment>
                <div>
                  { progressTitle }
                </div>
                {
                  !this.context.isMeasured ? (
                    <ProgressStatus
                      status={ progressTitle }
                      percentComplete={ progressPercentComplete }
                    />
                  ) : null
                }
              </Fragment>
            ) : null
          }
          {
            root ? (
              <ProgressCallout
                target={ root }
                onViewAll={ this._onClick }
                onDismiss={ this._onDismiss }
                isExecuting={ this.props.isExecuting }
                doNotExpire={ this.state.doNotExpire }
                itemKeys={ finishedProgressItemKeys }
              />
            ) : null
          }
        </DefaultRender>
      ) : null;
    }

    private _onButtonRef = (root: React.Component<{}> | null): void => {
      this.setState({
        root: root ? ReactDOM.findDOMNode(root) as HTMLElement : null
      });
    }

    private _onDismiss = (): void => {
      this.setState({
        doNotExpire: false
      });
    }

    private _onClick = (
      ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | undefined
    ): void => {
      this.setState({
        doNotExpire: true
      });

      const {
        item: {
          onClick
        },
      } = this.props;

      if (onClick) {
        onClick(ev);
      }
    }
  });

function getPercentComplete(itemCountsByProgressPhase: Histogram<ProgressPhase>): number | undefined {
  const totalCount =
    itemCountsByProgressPhase.created +
    itemCountsByProgressPhase.started +
    itemCountsByProgressPhase.completed +
    itemCountsByProgressPhase.failed;

  const completedCount =
    itemCountsByProgressPhase.completed +
    itemCountsByProgressPhase.failed;

  return totalCount > 0 ? completedCount / totalCount : undefined;
}

function getPercentCompleteDescription(percentComplete: number | undefined): string {
  if (typeof percentComplete === 'number') {
    return formatWithLocalizedCountValue(
      ProgressStrings.percentCompletedDescription,
      ProgressStrings.percentCompletedDescriptionIntervals,
      Math.round(percentComplete * 100));
  }

  return ProgressStrings.genericStartedIndeterminateDescription;
}
